import React, { useContext, useEffect, useState } from 'react'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Box, Button, Icon } from '@node-space/ui-components'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useIsSuspendedAccount } from 'hooks/useIsSuspendedAccount'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { WalletSheet } from '../Sheet/WalletSheet'

interface WalletHeaderButtonsProps {
  canEdit: boolean
  canTrade: boolean
  viewOnly: boolean
}

const WalletHeaderButtons = ({ canEdit, canTrade, viewOnly }: WalletHeaderButtonsProps) => {
  const { t } = useTranslation()
  const { enableUsLaunchRestrictions, enableCryptoFiatSend } = useFeatureFlags()
  const [, setWalletAction] = useContext(WalletActionContext)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const permissions = useMappedRoles()
  const canCreatePayOut = permissions?.payments?.canCreatePayOut
  const isSuspendedAccount = useIsSuspendedAccount()
  const { action } = queryString.parse(window.location.search) as { action?: ActionIds }
  const [searchParams, setSearchParams] = useSearchParams()

  const triggerOpenSheet = ({
    actionId,
    header,
    onClose,
  }: {
    actionId: ActionIds
    header: string
    onClose?: () => void
  }) => {
    setWalletAction({
      actionId: actionId,
      header: header,
      onClose: () => {
        track.Amp.track(AmplitudeEvent.EOF_CONVERT_EXIT, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.CLICK,
        })

        setIsSheetOpen(false)
        onClose?.()

        if (searchParams.has('action')) {
          searchParams.delete('action')
          setSearchParams(searchParams)
        }
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenCreateWalletSheet = () => {
    triggerOpenSheet({
      actionId: ActionIds?.WALLET_CREATE,
      header: t('wallets.addNewWallet'),
    })
  }

  const handleOpenConvertSheet = () => {
    track.Amp.track(AmplitudeEvent.CONVERT_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
    })
    triggerOpenSheet({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
    })
  }

  const handleOpenSendSheet = () => {
    triggerOpenSheet({
      actionId: ActionIds?.SEND,
      header: t('wallets.send.contactStepHeader'),
    })
  }

  useEffect(() => {
    switch (action) {
      case ActionIds?.WALLET_CREATE:
        handleOpenCreateWalletSheet()
        break
      case ActionIds?.CONVERT:
        handleOpenConvertSheet()
        break
      case ActionIds?.SEND:
        handleOpenSendSheet()
        break
    }
  }, [action])

  return (
    <Box flex gap={12}>
      {!viewOnly && canEdit && !isSuspendedAccount && !enableUsLaunchRestrictions && (
        <Button
          testId="buttonAddWallet"
          iconElement={<Icon name="Plus" />}
          onClick={handleOpenCreateWalletSheet}
        >
          {t('wallets.addNewWallet')}
        </Button>
      )}
      {canCreatePayOut && !isSuspendedAccount && (
        <Button
          testId="send-crypto"
          disabled={viewOnly}
          onClick={handleOpenSendSheet}
          iconElement={<Icon name="ArrowUpRight" />}
        >
          {enableCryptoFiatSend ? t('transactionAction.send') : t('payments.sendCrypto')}
        </Button>
      )}
      {!viewOnly && (canEdit || canTrade) && !isSuspendedAccount && !enableUsLaunchRestrictions && (
        <Button
          testId="buttonConvert"
          iconElement={<Icon name="ArrowsLeftRight" />}
          onClick={handleOpenConvertSheet}
        >
          {t('walletsPage.convert')}
        </Button>
      )}
      <WalletSheet isSheetOpen={isSheetOpen} />
    </Box>
  )
}

export default React.memo(WalletHeaderButtons)
